import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, MessageCircle, Copy, ArrowRight, ShoppingBag } from 'lucide-react';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { buildWhatsAppMessage, buildWhatsAppUrl } from '../lib/whatsapp';
import { formatCurrency } from '../lib/utils';
import { Restaurant, CartItem } from '../lib/types';
import { useLoyaltySummary } from '../lib/loyalty-hooks';
import { Sparkles, Gift } from 'lucide-react';
import { useLanguage } from '../lib/i18n';
import { supabase } from '../lib/supabase';
interface OrderState {
  restaurant: Restaurant;
  orderNumber: string;
  trackingToken: string;
  orderId?: string;
  customerName: string;
  customerPhone: string;
  deliveryType: string;
  paymentMethod?: string;
  deliveryAddress?: string | null;
  mapsUrl?: string | null;
  notes?: string | null;
  ticketImageUrl?: string | null;
  items: CartItem[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  customerTrackingVisible?: boolean;
}
export function OrderConfirmationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as OrderState | null;
  const [copied, setCopied] = useState(false);
  const [whatsappSent, setWhatsappSent] = useState(false);
  const {
    t
  } = useLanguage();
  if (!state) {
    return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-4405">
        <MarketplaceHeader data-id="element-4406" />
        <main className="flex-1 flex items-center justify-center py-12" data-id="element-4407">
          <EmptyState icon={ShoppingBag} title={t('confirm_no_order')} description="" actionLabel={t('confirm_back')} onAction={() => navigate('/')} data-id="element-4408" />
        </main>
        <Footer data-id="element-4409" />
      </div>;
  }
  const {
    restaurant,
    orderNumber,
    trackingToken,
    items,
    subtotal,
    deliveryFee,
    total
  } = state;
  const showTracking = state.customerTrackingVisible !== false;
  const {
    summary
  } = useLoyaltySummary(state.customerPhone, restaurant.id);
  const handleCopy = async () => {
    if (!restaurant.whatsapp_number) return;
    const message = buildWhatsAppMessage({
      restaurantName: restaurant.name,
      whatsappNumber: restaurant.whatsapp_number,
      currencySymbol: restaurant.currency_symbol,
      orderNumber: state.orderNumber,
      customerName: state.customerName,
      customerPhone: state.customerPhone,
      deliveryType: state.deliveryType,
      paymentMethod: state.paymentMethod,
      items: state.items,
      subtotal: state.subtotal,
      deliveryFee: state.deliveryFee,
      total: state.total,
      deliveryAddress: state.deliveryAddress,
      mapsUrl: state.mapsUrl,
      notes: state.notes,
      ticketImageUrl: state.ticketImageUrl,
      trackingToken: state.trackingToken,
      customerTrackingVisible: showTracking
    });
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text', err);
    }
  };
  const handleWhatsApp = async () => {
    if (!restaurant.whatsapp_number) return;
    // Update order status to 'new' so it appears in KDS
    if (state.orderId && !whatsappSent) {
      const {
        error: updateError
      } = await supabase.from('orders').update({
        status: 'new'
      }).eq('id', state.orderId);
      if (updateError) {
        console.error('[NOIZA] Error updating order status to new:', updateError);
        // Retry once with a small delay
        await new Promise(r => setTimeout(r, 500));
        const {
          error: retryError
        } = await supabase.from('orders').update({
          status: 'new'
        }).eq('id', state.orderId);
        if (retryError) {
          console.error('[NOIZA] Retry also failed:', retryError);
        } else {
          console.log('[NOIZA] Order status updated to new (retry):', state.orderId);
        }
      } else {
        console.log('[NOIZA] Order status updated to new:', state.orderId);
      }
    }
    const message = buildWhatsAppMessage({
      restaurantName: restaurant.name,
      whatsappNumber: restaurant.whatsapp_number,
      currencySymbol: restaurant.currency_symbol,
      orderNumber: state.orderNumber,
      customerName: state.customerName,
      customerPhone: state.customerPhone,
      deliveryType: state.deliveryType,
      paymentMethod: state.paymentMethod,
      items: state.items,
      subtotal: state.subtotal,
      deliveryFee: state.deliveryFee,
      total: state.total,
      deliveryAddress: state.deliveryAddress,
      mapsUrl: state.mapsUrl,
      notes: state.notes,
      ticketImageUrl: state.ticketImageUrl,
      trackingToken: state.trackingToken,
      customerTrackingVisible: showTracking
    });
    const url = buildWhatsAppUrl(restaurant.whatsapp_number, message);
    window.open(url, '_blank');
    setWhatsappSent(true);
  };
  const trackingUrl = `https://track.${restaurant.domain || 'restaurant.com'}/${trackingToken}`;
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(trackingUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy tracking link', err);
    }
  };
  return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-4410">
      <MarketplaceHeader data-id="element-4411" />

      <main className="flex-1 flex items-center justify-center p-4 py-12" data-id="element-4412">
        <div className="max-w-2xl mx-auto w-full" data-id="element-4413">
          {/* Header */}
          <div className="text-center mb-8" data-id="element-4414">
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${whatsappSent ? 'bg-green-100' : 'bg-amber-100'}`} data-id="element-4415">
              {whatsappSent ? <CheckCircle2 className="w-10 h-10 text-green-600" data-id="element-4416" /> : <MessageCircle className="w-10 h-10 text-amber-600" data-id="element-4417" />}
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-3" data-id="element-4418">
              {whatsappSent ? '¡Pedido enviado!' : '¡Ya casi está listo!'}
            </h1>
            <p className="text-xl text-gray-600" data-id="element-4419">
              {whatsappSent ? 'Tu pedido ha sido recibido por el restaurante' : `Orden #${orderNumber}`}
            </p>
          </div>

          {/* WhatsApp CTA — only before sending */}
          {!whatsappSent && restaurant.whatsapp_number && <div className="mb-8" data-id="element-4420">
              <button onClick={handleWhatsApp} className="w-full h-16 rounded-2xl text-xl font-black bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-lg shadow-emerald-200 flex items-center justify-center gap-3 transition-all animate-pulse" data-id="element-4421">
                <MessageCircle className="w-7 h-7" data-id="element-4422" />
                Enviar pedido por WhatsApp
              </button>

              <div className="mt-4 p-4 rounded-xl bg-red-50 border-2 border-red-300" data-id="element-4423">
                <p className="text-red-600 font-black text-base sm:text-lg text-center leading-tight" data-id="element-4424">
                  ⚠️ SI NO ENVÍAS POR WHATSAPP, TU PEDIDO NO SE REALIZA
                </p>
              </div>
            </div>}

          {/* Success confirmation after WhatsApp sent */}
          {whatsappSent && <div className="mb-8 p-5 rounded-2xl bg-green-50 border-2 border-green-200 text-center" data-id="element-4425">
              <p className="text-green-700 font-bold text-lg" data-id="element-4426">
                ✅ Tu pedido ha sido enviado al restaurante. ¡Gracias!
              </p>
            </div>}

          {/* Order Number Card */}
          <Card className="p-8 mb-8 text-center border-2 border-brand-primary/20 bg-brand-primary/5" data-id="element-4427">
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-4428">
              {t('confirm_order_number')}
            </p>
            <p className="text-5xl font-black text-brand-primary tracking-tight" data-id="element-4429">
              #{orderNumber}
            </p>
          </Card>

          {/* Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-4430">
            {showTracking && <Button onClick={() => navigate(`/track/${trackingToken}`)} className="h-14 text-lg bg-gray-900 hover:bg-gray-800 text-white" data-id="element-4431">
                <ShoppingBag className="w-5 h-5 mr-2" data-id="element-4432" />
                {t('confirm_track')}
              </Button>}

            {whatsappSent && restaurant.whatsapp_number && <Button onClick={handleWhatsApp} variant="outline" className="h-14 text-lg border-2 hover:bg-green-50 hover:text-green-600 hover:border-green-200" data-id="element-4433">
                <MessageCircle className="w-5 h-5 mr-2 text-green-600" data-id="element-4434" />
                Reenviar por WhatsApp
              </Button>}
          </div>

          {/* Tracking Link */}
          {showTracking && <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm mb-8" data-id="element-4435">
              <p className="text-sm font-medium text-gray-900 mb-4" data-id="element-4436">
                {t('copy_tracking')}
              </p>
              <div className="flex items-center gap-3" data-id="element-4437">
                <div className="flex-1 bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-500 truncate border border-gray-100" data-id="element-4438">
                  {trackingUrl}
                </div>
                <Button variant="outline" onClick={handleCopyLink} className="shrink-0" data-id="element-4439">
                  {copied ? <span className="text-green-600 flex items-center" data-id="element-4440">
                      <CheckCircle2 className="w-4 h-4 mr-2" data-id="element-4441" />
                      {t('confirm_copied')}
                    </span> : <>
                      <Copy className="w-4 h-4 mr-2" data-id="element-4442" />
                      {t('confirm_copy')}
                    </>}
                </Button>
              </div>
            </div>}

          {/* Loyalty Summary Block */}
          {summary && <div className="bg-gradient-to-r from-[#1A1A1A] to-[#2A2A2A] rounded-2xl p-6 text-white shadow-lg border border-[#333] mb-8" data-id="element-4443">
              <div className="flex items-center gap-3 mb-4" data-id="element-4444">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0" data-id="element-4445">
                  <Sparkles className="w-5 h-5 text-[#F5E9DA]" data-id="element-4446" />
                </div>
                <div data-id="element-4447">
                  <h3 className="font-bold text-lg" data-id="element-4448">¡Ganaste puntos!</h3>
                  <p className="text-sm text-white/70" data-id="element-4449">
                    Tu nivel actual: {summary.current_level || 'Inicial'}
                  </p>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-4 border border-white/10" data-id="element-4450">
                <p className="text-sm font-medium" data-id="element-4451">
                  Tienes un total de{' '}
                  <span className="font-bold text-[#F5E9DA]" data-id="element-4452">
                    {summary.current_points} puntos
                  </span>{' '}
                  acumulados.
                </p>
              </div>

              {summary.available_rewards_count > 0 && <div className="mt-4 flex items-start gap-3 bg-[#006847]/20 border border-[#006847]/30 rounded-xl p-4" data-id="element-4453">
                  <Gift className="w-5 h-5 text-[#006847] shrink-0 mt-0.5" data-id="element-4454" />
                  <p className="text-sm font-medium text-white" data-id="element-4455">
                    ¡Tienes {summary.available_rewards_count}{' '}
                    {summary.available_rewards_count === 1 ? 'beneficio disponible' : 'beneficios disponibles'}{' '}
                    para tu próxima compra!
                  </p>
                </div>}
            </div>}

          {/* Order Details */}
          <Card className="p-6 mb-8" data-id="element-4456">
            <h3 className="font-bold text-gray-900 mb-4" data-id="element-4457">
              {t('order_summary')}
            </h3>
            <div className="space-y-4" data-id="element-4458">
              <div className="flex justify-between" data-id="element-4459">
                <span className="text-gray-600" data-id="element-4460">
                  {t('confirm_order_number')}
                </span>
                <span className="font-semibold text-gray-900" data-id="element-4461">
                  {orderNumber}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-4462">
                <span className="text-gray-600" data-id="element-4463">{t('total')}</span>
                <span className="font-semibold text-gray-900" data-id="element-4464">
                  {formatCurrency(total)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-4465">
                <span className="text-gray-600" data-id="element-4466">{t('checkout_payment')}</span>
                <span className="text-gray-600" data-id="element-4467">
                  {state.paymentMethod || 'No especificado'}
                </span>
              </div>
              {state.deliveryAddress && <div className="flex justify-between" data-id="element-4468">
                  <span className="text-gray-600" data-id="element-4469">{t('checkout_address')}</span>
                  <span className="text-gray-600" data-id="element-4470">{state.deliveryAddress}</span>
                </div>}
            </div>
          </Card>

          {/* Bottom actions */}
          <div className="space-y-4" data-id="element-4471">
            {restaurant.whatsapp_number && <>
                <Button size="lg" fullWidth onClick={handleWhatsApp} className="bg-[#25D366] hover:bg-[#20bd5a] text-white border-none shadow-lg shadow-emerald-200 h-14 text-lg" data-id="element-4472">
                  <MessageCircle className="w-6 h-6 mr-2" data-id="element-4473" />
                  {whatsappSent ? 'Reenviar por WhatsApp' : t('confirm_whatsapp')}
                </Button>

                <Button variant="secondary" size="lg" fullWidth onClick={handleCopy} className="h-14" data-id="element-4474">
                  <Copy className="w-5 h-5 mr-2" data-id="element-4475" />
                  {copied ? t('confirm_copied') : t('confirm_copy')}
                </Button>
              </>}

            {showTracking && <Button variant="ghost" size="lg" fullWidth onClick={() => navigate(`/track/${trackingToken}`)} className="h-14 border border-gray-200" data-id="element-4476">
                {t('confirm_track')}
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-4477" />
              </Button>}
          </div>
        </div>
      </main>

      <Footer data-id="element-4478" />
    </div>;
}